import { Component, Inject, OnInit, signal, inject, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { WhitelabelChatService, AgentPromptTurn } from '../../../../../../../core/services/whitelabel-chat.service';
import { AuthService } from '../../../../../../../core/services/auth.service';
import { ButtonComponent } from '../../../../../../../shared/components/ui/button/button.component';
import { IconComponentV2 } from '../../../../../../../shared/components/icon-component/icon.component';

export interface PromptGeneratorModalData {
  chatbotId: number;
  initialPrompt?: string;
}

@Component({
  selector: 'app-prompt-generator-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, ButtonComponent, IconComponentV2],
  templateUrl: './prompt-generator-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .step-fade-in {
      animation: fadeIn 0.3s ease-in-out;
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `]
})
export class PromptGeneratorModalComponent implements OnInit {
  private chatService = inject(WhitelabelChatService);
  private authService = inject(AuthService);
  private dialogRef = inject(MatDialogRef<PromptGeneratorModalComponent>);
  
  constructor(@Inject(MAT_DIALOG_DATA) public data: PromptGeneratorModalData) {}

  state = signal<'initializing' | 'questionnaire' | 'generating' | 'completed' | 'error'>('initializing');
  questions = signal<string[]>([]);
  responses = signal<string[]>([]);
  currentIndex = signal(0);
  generatedPrompt = signal('');
  errorMessage = signal('');

  displayPrompt = computed(() => {
    let content = this.generatedPrompt().trim();
    
    // 1. Remove markdown fences (e.g. ```json ... ```)
    content = content.replace(/^```(json)?\s*/, '').replace(/\s*```$/, '').trim();
    
    // 2. Try to extract "system_prompt": "..." content manually to handle partial JSON
    if (content.startsWith('{')) {
      // Heuristic to extract content of system_prompt field even if JSON is incomplete
      const match = content.match(/"system_prompt"\s*:\s*"((?:[^"\\]|\\.)*)"/);
      if (match && match[1]) {
        // Unescape the captured string
        return match[1]
          .replace(/\\n/g, '\n')
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\')
          .replace(/\\t/g, '\t');
      }
      
      // If we can't find the field yet but we have JSON, it might still be generating the key
      return 'Analyzing persona details...';
    }

    return content;
  });

  stepCount = signal(1);
  skippedQuestions = signal<boolean[]>([]);
  sessionId = `pg_${Date.now()}`;

  ngOnInit() {
    this.startGeneration();
  }

  startGeneration() {
    this.state.set('initializing');
    this.stepCount.set(1);
    this.fetchNextStep([]);
  }

  fetchNextStep(information: AgentPromptTurn[]) {
    this.chatService.generateAgentSystemPrompt(
      { 
        chatbotId: this.data.chatbotId, 
        sessionId: this.sessionId, 
        information,
        userId: this.authService.getUserPid() || undefined 
      },
      {
        onQuestion: (qs) => {
          this.questions.set(qs);
          this.responses.set(qs.map(() => ''));
          this.skippedQuestions.set(qs.map(() => false));
          this.currentIndex.set(0);
          this.state.set('questionnaire');
        },
        onDelta: (delta) => {
          if (this.state() !== 'generating') {
            this.state.set('generating');
          }
          this.generatedPrompt.update(p => p + delta);
        },
        onComplete: (content) => {
          this.generatedPrompt.set(content);
          this.state.set('completed');
        },
        onError: (err) => {
          this.errorMessage.set(err.message);
          this.state.set('error');
        }
      }
    );
  }

  onNext() {
    // If user types something, clear skip flag for this index
    if (this.responses()[this.currentIndex()]?.trim()) {
      this.skippedQuestions.update(skips => {
        const next = [...skips];
        next[this.currentIndex()] = false;
        return next;
      });
    }

    if (this.currentIndex() < this.questions().length - 1) {
      this.currentIndex.update(i => i + 1);
    } else {
      this.submitResponses();
    }
  }

  onBack() {
    if (this.currentIndex() > 0) {
      this.currentIndex.update(i => i - 1);
    }
  }

  onSkipQuestion() {
    this.skippedQuestions.update(skips => {
      const next = [...skips];
      next[this.currentIndex()] = true;
      return next;
    });
    
    // If not last question, just go next. If last, submit.
    if (this.currentIndex() < this.questions().length - 1) {
      this.currentIndex.update(i => i + 1);
    } else {
      this.submitResponses();
    }
  }

  submitResponses() {
    const turns: AgentPromptTurn[] = this.questions().map((q, i) => ({
      question: q,
      response: this.responses()[i] || '',
      skipQuestion: this.skippedQuestions()[i] || !this.responses()[i]?.trim()
    }));
    this.submitWithData(turns);
  }

  private submitWithData(turns: AgentPromptTurn[]) {
    this.state.set('initializing');
    this.stepCount.update(c => c + 1);
    this.fetchNextStep(turns);
  }

  onApply() {
    this.dialogRef.close(this.displayPrompt());
  }

  onClose() {
    this.dialogRef.close();
  }
}
